import { useMutation } from '@apollo/client';
import { DELETE_COMMUNITY_ACTION, GET_COMMUNITY_ACTIONS } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

interface DeleteCommunityActionResponse {
  deleteCommunityAction: boolean;
}

export function useDeleteCommunityAction(communityId?: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [deleteActionMutation, { loading }] = useMutation<DeleteCommunityActionResponse>(DELETE_COMMUNITY_ACTION, {
    refetchQueries: communityId ? [{ query: GET_COMMUNITY_ACTIONS, variables: { communityId } }] : [],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const deleteAction = async (communityId: string, actionName: string): Promise<boolean> => {
    try {
      track('DeleteCommunityActionInitiated', {
        communityId,
        actionName,
      });

      await deleteActionMutation({
        variables: {
          communityId,
          actionName,
        },
      });

      track('DeleteCommunityActionSucceeded', {
        communityId,
        actionName,
      });

      return true;
    } catch (error) {
      track('DeleteCommunityActionFailed', {
        communityId,
        actionName,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    deleteAction,
    loading,
    errors,
    resetErrors,
  };
}

export default useDeleteCommunityAction;
